import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  AlertOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  CheckSquareOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  SolutionOutlined,
  UserAddOutlined,
  WarningFilled,
} from '@ant-design/icons'
import queryString from 'query-string'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { Skeleton } from 'antd'
import api from '../../api'
import { useAsync } from '../../hooks'
import { useIsAdmin } from '../../redux/slices/session'
import { toPath } from '../../routes'

const useStyles = createUseStyles({
  root: {
    marginBottom: 14,
  },
  panel: {
    borderRadius: 8, // antd borderRadiusLG
    padding: '10px 14px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
  },
  panelAttention: {
    background: '#f8fafc',
    borderColor: '#e2e8f0',
  },
  panelClear: {
    background: '#f0fdf4',
    borderColor: '#d1fadf',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    color: '#334155',
    letterSpacing: '-0.005em',
    flexShrink: 0,
  },
  count: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    borderRadius: 4, // antd borderRadiusSM
    background: '#64748b',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: 0,
  },
  divider: {
    width: 1,
    height: 20,
    background: 'rgba(15,23,42,0.08)',
    flexShrink: 0,
  },
  items: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  item: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 10px 4px 6px',
    borderRadius: 6, // antd borderRadius
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(15,23,42,0.06)',
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'background-color, transform, border-color',
    transitionDuration: '180ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
    '&:hover': {
      background: '#fff',
      borderColor: 'rgba(15,23,42,0.12)',
      '& $itemArrow': {
        opacity: 1,
        transform: 'translateX(0)',
      },
    },
    '&:active': {
      transform: 'scale(0.97)',
    },
  },
  itemIcon: {
    width: 22,
    height: 22,
    borderRadius: 4, // antd borderRadiusSM
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    flexShrink: 0,
  },
  itemBody: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 5,
    minWidth: 0,
  },
  itemValue: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    color: '#0f172a',
  },
  itemLabel: {
    fontSize: 12,
    color: '#475569',
    whiteSpace: 'nowrap',
  },
  itemArrow: {
    color: '#94a3b8',
    fontSize: 10,
    opacity: 0.4,
    transform: 'translateX(-2px)',
    transitionProperty: 'opacity, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
  },
  clear: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: '#166534',
    fontSize: 13,
    fontWeight: 500,
  },
  clearIcon: {
    color: '#22c55e',
    fontSize: 15,
  },
  loadingRow: {
    display: 'flex',
    gap: 8,
    flex: 1,
    flexWrap: 'wrap',
  },
  loadingItem: {
    height: 30,
    borderRadius: 6, // antd borderRadius
  },
})

const SEVERITY_STYLES = {
  critical: { bg: '#fee2e2', color: '#dc2626' },
  warning: { bg: '#fef3c7', color: '#d97706' },
  neutral: { bg: '#e2e8f0', color: '#475569' },
  info: { bg: '#dbeafe', color: '#2563eb' },
}

function AttentionItem({ to, icon, severity, value = null, label }) {
  const classes = useStyles()
  const { bg, color } = SEVERITY_STYLES[severity] || SEVERITY_STYLES.info
  return (
    <Link to={to} className={classes.item}>
      <div className={classes.itemIcon} style={{ background: bg, color }}>
        {icon}
      </div>
      <div className={classes.itemBody}>
        {value != null && (
          <span className={classes.itemValue}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
        )}
        <span className={classes.itemLabel}>{label}</span>
      </div>
      <ArrowRightOutlined className={classes.itemArrow} />
    </Link>
  )
}

AttentionItem.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  severity: PropTypes.oneOf(['critical', 'warning', 'neutral', 'info']).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
}

export default function NeedsAttention({ preAuthorizationStats = [] }) {
  const classes = useStyles()
  const isAdmin = useIsAdmin()

  const fetchAlerts = useCallback(() => api.alerts.getUnacknowledgedCount(), [])
  const fetchPending = useCallback(
    () => api.reports.getPendings({ page: 1 }),
    [],
  )
  const fetchUnreviewed = useCallback(
    () => api.reports.getUnreviewed({ page: 1 }),
    [],
  )
  const fetchUnapproved = useCallback(
    () => api.patients.getUnapprovedCount(),
    [],
  )

  const { value: alertsData, pending: alertsPending } = useAsync(fetchAlerts)
  const { value: pendingReports, pending: pendingLoading } = useAsync(
    fetchPending,
    !isAdmin,
  )
  const { value: unreviewedReports, pending: unreviewedLoading } = useAsync(
    fetchUnreviewed,
    !isAdmin,
  )
  const { value: unapprovedData, pending: unapprovedLoading } = useAsync(
    fetchUnapproved,
    isAdmin,
  )

  const unackedAlerts = alertsData?.count ?? 0
  const unapprovedPatients = unapprovedData?.count ?? 0

  const pendingCount = useMemo(() => {
    if (!pendingReports?.headers) return 0
    return Number(pendingReports.headers.get('X-Total')) || 0
  }, [pendingReports])

  const unreviewedCount = useMemo(() => {
    if (!unreviewedReports?.headers) return 0
    return Number(unreviewedReports.headers.get('X-Total')) || 0
  }, [unreviewedReports])

  const pendingPreAuths = useMemo(() => {
    if (!preAuthorizationStats?.length) return 0
    const latest = preAuthorizationStats[preAuthorizationStats.length - 1]
    return Math.max(0, (latest.count || 0) - (latest.authorizedCount || 0))
  }, [preAuthorizationStats])

  const unapprovedPath = useMemo(
    () => queryString.stringifyUrl({
      url: toPath.patients(),
      query: { approved: 'false' },
    }),
    [],
  )

  const items = useMemo(() => [
    unackedAlerts > 0 && {
      key: 'alerts',
      to: `${toPath.alerts()}?acknowledged=false`,
      icon: <WarningFilled />,
      severity: 'critical',
      value: unackedAlerts,
      label: `Unacknowledged alert${unackedAlerts === 1 ? '' : 's'}`,
    },
    !isAdmin && pendingCount > 0 && {
      key: 'pending',
      to: toPath.pendingReports(),
      icon: <FileDoneOutlined />,
      severity: 'warning',
      value: pendingCount,
      label: `Pending report${pendingCount === 1 ? '' : 's'}`,
    },
    !isAdmin && unreviewedCount > 0 && {
      key: 'unreviewed',
      to: toPath.unreviewedReports(),
      icon: <FileSearchOutlined />,
      severity: 'warning',
      value: unreviewedCount,
      label: `Unreviewed report${unreviewedCount === 1 ? '' : 's'}`,
    },
    isAdmin && pendingPreAuths > 0 && {
      key: 'preauth',
      to: toPath.preAuthorizations(),
      icon: <SolutionOutlined />,
      severity: 'warning',
      value: pendingPreAuths,
      label: `Pre-auth${pendingPreAuths === 1 ? '' : 's'} pending this month`,
    },
    isAdmin && unapprovedPatients > 0 && {
      key: 'unapproved',
      to: unapprovedPath,
      icon: <UserAddOutlined />,
      severity: 'warning',
      value: unapprovedPatients,
      label: `Patient${unapprovedPatients === 1 ? '' : 's'} pending approval`,
    },
    isAdmin && {
      key: 'missed-transmissions',
      to: toPath.missedTransmissions(),
      icon: <AlertOutlined />,
      severity: 'neutral',
      value: null,
      label: 'Missed transmissions',
    },
  ].filter(Boolean), [
    unackedAlerts,
    pendingCount,
    unreviewedCount,
    pendingPreAuths,
    unapprovedPatients,
    unapprovedPath,
    isAdmin,
  ])

  const loading =
    alertsPending ||
    (!isAdmin && (pendingLoading || unreviewedLoading)) ||
    (isAdmin && unapprovedLoading)

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={`${classes.panel} ${classes.panelAttention}`}>
          <div className={classes.header}>
            <CheckSquareOutlined style={{ color: '#64748b' }} />
            <span>To do</span>
          </div>
          <div className={classes.divider} />
          <div className={classes.loadingRow}>
            <Skeleton.Button active style={{ height: 28, width: 140, borderRadius: 6 }} />
            <Skeleton.Button active style={{ height: 28, width: 140, borderRadius: 6 }} />
          </div>
        </div>
      </div>
    )
  }

  const urgentItems = items.filter(i => i.severity !== 'neutral')
  const hasUrgent = urgentItems.length > 0

  if (items.length === 0) {
    return (
      <div className={classes.root}>
        <div className={`${classes.panel} ${classes.panelClear}`}>
          <div className={classes.clear}>
            <CheckCircleFilled className={classes.clearIcon} />
            <span>All done — your list is clear.</span>
          </div>
        </div>
      </div>
    )
  }

  const totalCount = urgentItems.reduce(
    (sum, item) => sum + (typeof item.value === 'number' ? item.value : 0),
    0,
  )

  return (
    <div className={classes.root}>
      <div
        className={`${classes.panel} ${
          hasUrgent ? classes.panelAttention : classes.panelClear
        }`}
      >
        <div className={classes.header}>
          {hasUrgent ? (
            <CheckSquareOutlined style={{ color: '#64748b' }} />
          ) : (
            <CheckCircleFilled style={{ color: '#22c55e' }} />
          )}
          <span>{hasUrgent ? 'To do' : 'All done'}</span>
          {hasUrgent && totalCount > 0 && (
            <span className={classes.count}>{totalCount.toLocaleString()}</span>
          )}
        </div>
        <div className={classes.divider} />
        <div className={classes.items}>
          {items.map(item => (
            <AttentionItem
              key={item.key}
              to={item.to}
              icon={item.icon}
              severity={item.severity}
              value={item.value}
              label={item.label}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

NeedsAttention.propTypes = {
  preAuthorizationStats: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      authorizedCount: PropTypes.number,
    }),
  ),
}
